import React, { useMemo } from 'react';
import { Box } from '@common-fe/common-fe';

import { IPlanDetailDtoFundingMethodEnum } from '@/dtos/models/IPlanDetailDto';
import { BankingBalanceFundingMethodSpecific } from '@/modules/employer/components/BankingBalance/types';

import BankingBalanceDetailRow from '../BankingBalanceOrgDetails/BankingBalanceDetailRow';

interface Props {
  funding?: BankingBalanceFundingMethodSpecific;
  fundingMethod?: string;
}

const BankingBalancePlanFunding: React.FC<Props> = ({ funding, fundingMethod }) => {
  const showPrefund = useMemo(
    () => fundingMethod !== IPlanDetailDtoFundingMethodEnum.CONTRIBUTION_ONLY,
    [fundingMethod]
  );

  const showContribution = useMemo(
    () => fundingMethod === IPlanDetailDtoFundingMethodEnum.CONTRIBUTION_ONLY
      || fundingMethod === IPlanDetailDtoFundingMethodEnum.PREFUND_AND_CONTRIBUTION,
    [fundingMethod]
  );

  const showReplenishment = useMemo(
    () => fundingMethod === IPlanDetailDtoFundingMethodEnum.STANDARD_NOTIONAL,
    [fundingMethod]
  );

  if (!funding) return null;

  return (
    <Box data-testid="plan-funding">
      {showPrefund && (
        <Box gap="spacing4">
          <BankingBalanceDetailRow
            testId="plan-funding_calculatedPrefund"
            label="Plan calculated prefund"
            value={funding.calculatedPrefund}
          />
          <BankingBalanceDetailRow
            testId="plan-funding_prefundHolding"
            label="Plan prefund holding"
            value={funding.prefundHolding}
          />
        </Box>
      )}

      {showReplenishment && (
        <>
          <Box height="1px" background="border2" margin={{ vertical: 'spacing4' }} />
          <Box gap="spacing4">
            <BankingBalanceDetailRow
              testId="plan-funding_replenishmentToBeInvoiced"
              label="Plan replenishment to be invoiced"
              description="Current claim invoice accrual"
              value={funding.replenishmentToBeInvoiced}
            />
            <BankingBalanceDetailRow
              testId="plan-funding_replenishmentNotReceived"
              label="Plan replenishment not received"
              description="Claim invoice requested but funds not received"
              value={funding.replenishmentNotReceived}
            />
          </Box>
        </>
      )}

      {showContribution && (
        <>
          {showPrefund && <Box height="1px" background="border2" margin={{ vertical: 'spacing4' }} />}
          <Box gap="spacing4">
            <BankingBalanceDetailRow
              testId="plan-funding_contributionsTotalReceived"
              label="Contribution total received"
              value={funding.contributionsTotalReceived}
            />
            <BankingBalanceDetailRow
              testId="plan-funding_contributionsNotYetReceived"
              label="Contributions not received"
              description="Contribution invoice requested but funds not received"
              value={funding.contributionsNotYetReceived}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default BankingBalancePlanFunding;
